import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { toast } from 'sonner';
import { downloadAttachment, prepareAttachmentUntilReady } from '@/api/emails';
import type { Attachment, Email, ThreadMessage } from '@/types/email';
import { useAttachmentStore } from '@/stores/attachmentStore';

type EmailLike = Email | ThreadMessage | null;

const INLINE_PREVIEW_MAX_SIZE = 25 * 1024 * 1024; // 25 MB
const MAX_INLINE_DOWNLOAD_CONCURRENCY = 3;
const ATTACHMENT_PREPARE_TTL = 5 * 60 * 1000; // 5 minutes

export interface InlineAttachmentsState {
  attachments: Attachment[];
  cidMap: Record<string, string>;
  previewing: Record<string, string>;
  downloading: Record<string, boolean>;
  handleTogglePreview: (attachment: Attachment) => Promise<void> | void;
  handleDownload: (attachment: Attachment) => Promise<void>;
  isPreviewable: (mimeType: string) => boolean;
}

export function useInlineAttachments(
  email: EmailLike,
  explicitAccountId: string | null,
): InlineAttachmentsState {
  const markAttachmentDownloaded = useAttachmentStore((state) => state.markDownloaded);
  const setAttachmentBlob = useAttachmentStore((state) => state.setAttachmentBlob);
  const getAttachmentBlob = useAttachmentStore((state) => state.getAttachmentBlob);
  const [downloadingAttachments, setDownloadingAttachments] = useState<Record<string, boolean>>({});
  const [previewingAttachments, setPreviewingAttachments] = useState<Record<string, string>>({});
  const [inlineAttachmentUrls, setInlineAttachmentUrls] = useState<Record<string, string>>({});
  const [failedInlineIds, setFailedInlineIds] = useState<Set<string>>(new Set());

  const previewUrlsRef = useRef<Record<string, string>>({});
  const inlineAttachmentUrlsRef = useRef<Record<string, string>>({});
  const preparedAttachmentTimestampsRef = useRef<Map<string, number>>(new Map());
  const preparationPromisesRef = useRef<Map<string, Promise<void>>>(new Map());
  // Track which email IDs have been fully processed to avoid re-downloading on unrelated email updates
  const processedInlineAttachmentsRef = useRef<string | null>(null);

  const effectiveAccountId = explicitAccountId ?? email?.accountId ?? null;
  const emailId = email?.id ?? null;

  const referencedInlineAttachmentIds = useMemo(() => {
    if (!email?.attachments?.length || !email?.bodyHtml) {
      return new Set<string>();
    }
    const referencedCids = extractReferencedCids(email.bodyHtml);
    if (!referencedCids.size) {
      return new Set<string>();
    }
    const referencedIds = new Set<string>();
    email.attachments.forEach((attachment) => {
      if (!attachment.inline || !attachment.contentId) {
        return;
      }
      const normalizedCid = normalizeContentIdForComparison(attachment.contentId);
      if (normalizedCid && referencedCids.has(normalizedCid)) {
        referencedIds.add(attachment.id);
      }
    });
    return referencedIds;
  }, [email?.attachments, email?.bodyHtml]);

  const updateInlineAttachmentUrls = useCallback((next: Record<string, string>) => {
    setInlineAttachmentUrls((previous) => {
      // Bail out when nothing actually changed to avoid triggering re-renders
      const prevKeys = Object.keys(previous);
      const nextKeys = Object.keys(next);
      if (prevKeys.length === nextKeys.length && nextKeys.every((k) => previous[k] === next[k])) {
        return previous;
      }
      Object.entries(previous).forEach(([id, url]) => {
        if ((!next[id] || next[id] !== url) && url && url.startsWith('blob:')) {
          window.URL.revokeObjectURL(url);
        }
      });
      inlineAttachmentUrlsRef.current = next;
      return next;
    });
  }, []);

  const releasePreviewUrl = useCallback((attachmentId: string) => {
    const existing = previewUrlsRef.current[attachmentId];
    if (existing && existing.startsWith('blob:')) {
      window.URL.revokeObjectURL(existing);
    }
    if (existing) {
      delete previewUrlsRef.current[attachmentId];
    }
  }, []);

  useEffect(() => {
    return () => {
      Object.values(previewUrlsRef.current).forEach((url) => {
        if (url && url.startsWith('blob:')) {
          window.URL.revokeObjectURL(url);
        }
      });
      previewUrlsRef.current = {};

      Object.values(inlineAttachmentUrlsRef.current).forEach((url) => {
        if (url && url.startsWith('blob:')) {
          window.URL.revokeObjectURL(url);
        }
      });
      inlineAttachmentUrlsRef.current = {};
    };
  }, []);

  useEffect(() => {
    setPreviewingAttachments({});
    setDownloadingAttachments({});
    setFailedInlineIds(new Set());

    Object.values(previewUrlsRef.current).forEach((url) => {
      if (url && url.startsWith('blob:')) {
        window.URL.revokeObjectURL(url);
      }
    });
    previewUrlsRef.current = {};

    updateInlineAttachmentUrls({});
    preparedAttachmentTimestampsRef.current.clear();
    preparationPromisesRef.current.clear();
    processedInlineAttachmentsRef.current = null;
  }, [email?.id, updateInlineAttachmentUrls]);

  const ensureAttachmentPrepared = useCallback(
    async (attachment: Attachment) => {
      if (!emailId || !effectiveAccountId) {
        return;
      }
      const key = `${emailId}:${attachment.id}`;
      const lastPrepared = preparedAttachmentTimestampsRef.current.get(key);
      if (lastPrepared && Date.now() - lastPrepared < ATTACHMENT_PREPARE_TTL) {
        return;
      }

      const existingPromise = preparationPromisesRef.current.get(key);
      if (existingPromise) {
        return existingPromise;
      }

      const promise = prepareAttachmentUntilReady(effectiveAccountId, emailId, attachment.id)
        .then(() => {
          preparedAttachmentTimestampsRef.current.set(key, Date.now());
        })
        .catch((error) => {
          preparedAttachmentTimestampsRef.current.delete(key);
          console.error('Failed to prepare attachment download', error);
          throw error;
        })
        .finally(() => {
          preparationPromisesRef.current.delete(key);
        });

      preparationPromisesRef.current.set(key, promise);
      return promise;
    },
    [emailId, effectiveAccountId],
  );

  useEffect(() => {
    if (!email || !effectiveAccountId || !email.attachments || email.attachments.length === 0) {
      return;
    }
    if (referencedInlineAttachmentIds.size === 0) {
      return;
    }

    // P8 (PERF_AUDIT): eagerly prepare ONLY attachments actually referenced
    // inline in the body (cid:). Previously EVERY attachment fired a
    // POST /attachments/:id/prepare on open — a needless request burst for
    // ordinary (non-inline) attachments. Those now prepare on user action:
    // handleDownload / handleTogglePreview both call ensureAttachmentPrepared.
    email.attachments.forEach((attachment) => {
      if (!attachment || !referencedInlineAttachmentIds.has(attachment.id)) {
        return;
      }
      const preparation = ensureAttachmentPrepared(attachment);
      if (preparation) {
        preparation.catch(() => {});
      }
    });
  }, [email, effectiveAccountId, ensureAttachmentPrepared, referencedInlineAttachmentIds]);

  useEffect(() => {
    if (!email || !effectiveAccountId || !email.attachments || email.attachments.length === 0) {
      updateInlineAttachmentUrls({});
      return;
    }

    if (referencedInlineAttachmentIds.size === 0) {
      updateInlineAttachmentUrls({});
      return;
    }

    // Skip if we've already processed inline attachments for this email
    // This prevents re-downloading when unrelated email fields change (e.g., read status)
    if (processedInlineAttachmentsRef.current === email.id) {
      return;
    }

    const inlineCandidates = email.attachments.filter((attachment) => {
      if (!referencedInlineAttachmentIds.has(attachment.id)) {
        return false;
      }
      if (attachment.validated === false) {
        return false;
      }
      if (!attachment.contentId) {
        return false;
      }
      if (!isPreviewableMimeType(attachment.mimeType)) {
        return false;
      }
      if (typeof attachment.size === 'number' && attachment.size > INLINE_PREVIEW_MAX_SIZE) {
        return false;
      }
      return true;
    });

    if (inlineCandidates.length === 0) {
      updateInlineAttachmentUrls({});
      return;
    }

    let cancelled = false;
    let currentIndex = 0;

    const createdUrls: string[] = [];
    const urls: Record<string, string> = {};

    const workerCount = Math.min(MAX_INLINE_DOWNLOAD_CONCURRENCY, inlineCandidates.length);
    let hadFailures = false;
    const workers = Array.from({ length: workerCount }).map(async () => {
      while (!cancelled) {
        const nextIndex = currentIndex;
        currentIndex += 1;
        const attachment = inlineCandidates[nextIndex];
        if (!attachment) {
          break;
        }

        // Reuse URL from a previous partial run if available
        const existingUrl = inlineAttachmentUrlsRef.current[attachment.id];
        if (existingUrl) {
          urls[attachment.id] = existingUrl;
          continue;
        }

        if (attachment.previewUrl) {
          urls[attachment.id] = attachment.previewUrl;
          continue;
        }

        try {
          const cacheEntry = getAttachmentBlob(
            effectiveAccountId,
            email.id,
            attachment.id,
          );

          let blobEntry = cacheEntry;
          if (!blobEntry) {
            const { blob, filename: serverFilename } = await downloadAttachment(
              effectiveAccountId,
              email.id,
              attachment.id,
              email.folderId ?? '',
              { preview: true },
            );
            if (cancelled) {
              break;
            }
            blobEntry = {
              blob,
              filename: serverFilename ?? attachment.filename ?? null,
              mimeType: attachment.mimeType ?? null,
              size: typeof attachment.size === 'number' ? attachment.size : blob.size ?? null,
              createdAt: Date.now(),
            };
            setAttachmentBlob(effectiveAccountId, email.id, attachment.id, {
              blob: blobEntry.blob,
              filename: blobEntry.filename,
              mimeType: blobEntry.mimeType,
              size: blobEntry.size ?? blobEntry.blob.size ?? null,
            });
          }

          markAttachmentDownloaded(effectiveAccountId, email.id, attachment.id);
          if (cancelled) {
            break;
          }
          const objectUrl = window.URL.createObjectURL(blobEntry.blob);
          createdUrls.push(objectUrl);
          urls[attachment.id] = objectUrl;
        } catch (error) {
          if (!cancelled) {
            console.error('Failed to load inline attachment', error);
            hadFailures = true;
            setFailedInlineIds((prev) => new Set(prev).add(attachment.id));
          }
        }
      }
    });

    (async () => {
      await Promise.all(workers);
      if (cancelled) {
        createdUrls.forEach((url) => {
          if (url.startsWith('blob:')) {
            window.URL.revokeObjectURL(url);
          }
        });
        return;
      }
      updateInlineAttachmentUrls(urls);
      // Only mark as fully processed when all attachments loaded successfully.
      // If any failed, leave unset so the effect re-runs on next email object
      // change and retries the failures (successful ones are served from cache).
      if (!hadFailures) {
        processedInlineAttachmentsRef.current = email.id;
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [
    email,
    effectiveAccountId,
    getAttachmentBlob,
    markAttachmentDownloaded,
    referencedInlineAttachmentIds,
    setAttachmentBlob,
    updateInlineAttachmentUrls,
  ]);

  const attachmentsToDisplay = useMemo(() => {
    if (!email?.attachments) {
      return [];
    }
    return email.attachments.filter((attachment) => {
      if (attachment.validated === false) {
        return false;
      }
      // Filter out ALL inline attachments, not just CID-referenced ones
      // Many emails have inline images that aren't CID-referenced but still shouldn't show as attachments
      if (attachment.inline) {
        return false;
      }
      return true;
    }).map((attachment) => {
      // Fix missing or incorrect MIME types by falling back to file extension
      if (!attachment.mimeType || attachment.mimeType === 'application/octet-stream') {
        const ext = attachment.filename?.split('.').pop()?.toLowerCase();
        const mimeType = ext ? EXTENSION_MIME_FALLBACKS[ext] : undefined;
        if (mimeType) {
          return { ...attachment, mimeType };
        }
      }
      return attachment;
    });
  }, [email]);

  const cidMap = useMemo(() => {
    if (!email || !email.attachments) {
      return {};
    }
    // 1x1 transparent PNG as fallback for failed inline images
    const TRANSPARENT_PIXEL =
      'data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAAEAAAABCAQAAAC1HAwCAAAAC0lEQVR42mNkYAAAAAYAAjCB0C8AAAAASUVORK5CYII=';
    const map: Record<string, string> = {};
    email.attachments.forEach((attachment) => {
      if (attachment.validated === false || !attachment.contentId) {
        return;
      }
      if (attachment.inline && !referencedInlineAttachmentIds.has(attachment.id)) {
        return;
      }
      const cid = attachment.contentId.replace(/^<|>$/g, '');
      const source = inlineAttachmentUrls[attachment.id] ?? attachment.previewUrl ?? null;
      if (source) {
        map[cid] = source;
      } else if (failedInlineIds.has(attachment.id)) {
        // Use transparent placeholder so broken image icon doesn't show
        map[cid] = TRANSPARENT_PIXEL;
      }
    });
    return map;
  }, [email, inlineAttachmentUrls, referencedInlineAttachmentIds, failedInlineIds]);

  const handleDownload = useCallback(
    async (attachment: Attachment) => {
      if (!email || !effectiveAccountId) {
        return;
      }
      setDownloadingAttachments((previous) => ({ ...previous, [attachment.id]: true }));
      let objectUrl: string | null = null;

      try {
        let cacheEntry = getAttachmentBlob(effectiveAccountId, email.id, attachment.id);
        if (!cacheEntry) {
          await ensureAttachmentPrepared(attachment);
          const { blob, filename: serverFilename } = await downloadAttachment(
            effectiveAccountId,
            email.id,
            attachment.id,
            email.folderId ?? '',
          );
          cacheEntry = {
            blob,
            filename: serverFilename ?? attachment.filename ?? null,
            mimeType: attachment.mimeType ?? blob.type ?? null,
            size: typeof attachment.size === 'number' ? attachment.size : blob.size ?? null,
            createdAt: Date.now(),
          };
          setAttachmentBlob(effectiveAccountId, email.id, attachment.id, {
            blob: cacheEntry.blob,
            filename: cacheEntry.filename,
            mimeType: cacheEntry.mimeType,
            size: cacheEntry.size ?? cacheEntry.blob.size ?? null,
          });
        }
        markAttachmentDownloaded(effectiveAccountId, email.id, attachment.id);
        objectUrl = window.URL.createObjectURL(cacheEntry.blob);
        const anchor = document.createElement('a');
        anchor.href = objectUrl;
        anchor.download = getDownloadFilename(cacheEntry.filename ?? attachment.filename);
        anchor.rel = 'noopener';
        anchor.style.display = 'none';
        document.body.appendChild(anchor);
        anchor.click();
        document.body.removeChild(anchor);
      } catch (error) {
        console.error('Failed to download attachment', error);
        toast.error('Failed to download attachment. Please try again.');
      } finally {
        if (objectUrl) {
          window.URL.revokeObjectURL(objectUrl);
        }
        setDownloadingAttachments((previous) => ({ ...previous, [attachment.id]: false }));
      }
    },
    [
      email,
      effectiveAccountId,
      ensureAttachmentPrepared,
      getAttachmentBlob,
      markAttachmentDownloaded,
      setAttachmentBlob,
    ],
  );

  const handleTogglePreview = useCallback(
    async (attachment: Attachment) => {
      if (!email || !effectiveAccountId) {
        return;
      }

      const existing = previewingAttachments[attachment.id];
      if (existing) {
        releasePreviewUrl(attachment.id);
        setPreviewingAttachments((previous) => {
          const next = { ...previous };
          delete next[attachment.id];
          return next;
        });
        return;
      }

      const inlineUrl = inlineAttachmentUrlsRef.current[attachment.id];
      if (inlineUrl) {
        setPreviewingAttachments((previous) => ({ ...previous, [attachment.id]: inlineUrl }));
        return;
      }

      if (attachment.previewUrl) {
        setPreviewingAttachments((previous) => ({
          ...previous,
          [attachment.id]: attachment.previewUrl as string,
        }));
        return;
      }

      const cached = getAttachmentBlob(effectiveAccountId, email.id, attachment.id);
      if (cached) {
        const objectUrl = window.URL.createObjectURL(cached.blob);
        previewUrlsRef.current[attachment.id] = objectUrl;
        setPreviewingAttachments((previous) => ({ ...previous, [attachment.id]: objectUrl }));
        return;
      }

      try {
        await ensureAttachmentPrepared(attachment);
        const { blob, filename: serverFilename } = await downloadAttachment(
          effectiveAccountId,
          email.id,
          attachment.id,
          email.folderId ?? '',
          { preview: true },
        );
        let createdUrl: string | null = null;
        try {
          const objectUrl = window.URL.createObjectURL(blob);
          createdUrl = objectUrl;
          markAttachmentDownloaded(effectiveAccountId, email.id, attachment.id);
          setAttachmentBlob(effectiveAccountId, email.id, attachment.id, {
            blob,
            filename: serverFilename ?? attachment.filename ?? null,
            mimeType: attachment.mimeType ?? null,
            size: typeof attachment.size === 'number' ? attachment.size : blob.size ?? null,
          });
          previewUrlsRef.current[attachment.id] = objectUrl;
          setPreviewingAttachments((previous) => ({ ...previous, [attachment.id]: objectUrl }));
        } catch (error) {
          if (createdUrl) {
            window.URL.revokeObjectURL(createdUrl);
          }
          throw error;
        }
      } catch (error) {
        console.error('Failed to preview attachment', error);
        toast.error('Failed to preview attachment. Please try again.');
      }
    },
    [
      email,
      effectiveAccountId,
      ensureAttachmentPrepared,
      getAttachmentBlob,
      markAttachmentDownloaded,
      previewingAttachments,
      releasePreviewUrl,
      setAttachmentBlob,
    ],
  );

  return {
    attachments: attachmentsToDisplay,
    cidMap,
    previewing: previewingAttachments,
    downloading: downloadingAttachments,
    handleTogglePreview,
    handleDownload,
    isPreviewable: isPreviewableMimeType,
  };
}

const EXTENSION_MIME_FALLBACKS: Record<string, string> = {
  pdf: 'application/pdf',
  csv: 'text/csv',
  xls: 'application/vnd.ms-excel',
  xlsx: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  docx: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
};

function getDownloadFilename(filename?: string | null): string {
  const fallback = 'attachment';
  if (!filename) {
    return fallback;
  }
  const sanitized = filename.replace(/[\\/:*?"<>|]+/g, '_').trim();
  return sanitized || fallback;
}

// Some senders label CSVs as application/vnd.ms-excel; SheetJS handles both.
export const SPREADSHEET_MIME_TYPES = new Set([
  'text/csv',
  'application/vnd.ms-excel',
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
]);

// Modern .docx only — legacy binary .doc has no browser-side parser, so it stays download-only.
export const DOCX_MIME_TYPE = 'application/vnd.openxmlformats-officedocument.wordprocessingml.document';

export function isPreviewableMimeType(mimeType?: string | null): boolean {
  if (!mimeType) {
    return false;
  }
  const normalized = mimeType.toLowerCase();
  return (
    normalized.startsWith('image/') ||
    normalized === 'application/pdf' ||
    normalized === 'text/plain' ||
    normalized === 'text/html' ||
    normalized === DOCX_MIME_TYPE ||
    SPREADSHEET_MIME_TYPES.has(normalized)
  );
}

function extractReferencedCids(html: string): Set<string> {
  const matches = new Set<string>();
  const regex = /cid:([^"'()[\]\s>]+)/gi;
  let match: RegExpExecArray | null;
  while ((match = regex.exec(html)) !== null) {
    const candidate = match[1]?.trim();
    if (!candidate) {
      continue;
    }
    const normalized = normalizeContentIdForComparison(candidate);
    if (normalized) {
      matches.add(normalized);
    }
  }
  return matches;
}

function normalizeContentIdForComparison(contentId?: string | null): string | null {
  if (!contentId) {
    return null;
  }
  const stripped = contentId.replace(/^<|>$/g, '').trim();
  if (!stripped) {
    return null;
  }
  return stripped.toLowerCase();
}
